import { useEffect, useState } from "react";
import {
  Button,
  Combobox,
  Input,
  Option,
  Text,
  ToggleButton,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import type { UseConversation } from "../useConversation";
import { UploadPanel } from "./UploadPanel";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    ...shorthands.padding("12px"),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
  },
  pills: { display: "flex", flexWrap: "wrap", gap: "8px" },
  row: { display: "flex", gap: "8px", alignItems: "flex-end" },
  grow: { flexGrow: 1 },
  hint: { color: tokens.colorNeutralForeground3 },
  freeText: { display: "flex", gap: "8px", alignItems: "flex-end" },
});

type Conv = Pick<
  UseConversation,
  | "lastQuestion"
  | "stagedFiles"
  | "send"
  | "confirmChoice"
  | "confirmMulti"
  | "confirmDate"
  | "addFiles"
  | "removeFile"
  | "submitImageAnswer"
>;

export function QuestionInput({ conv, disabled }: { conv: Conv; disabled: boolean }) {
  const styles = useStyles();
  const { dict, id, text } = conv.lastQuestion;
  const questionKey = String(id ?? text ?? "");

  // Local widget state, reset whenever the current question changes.
  const [selectedOne, setSelectedOne] = useState<string | null>(null);
  const [selectedMulti, setSelectedMulti] = useState<string[]>([]);
  const [dropdownValue, setDropdownValue] = useState<string>("");
  const [dateValue, setDateValue] = useState<string>("");
  const [freeText, setFreeText] = useState<string>("");

  useEffect(() => {
    setSelectedOne(null);
    setSelectedMulti([]);
    setDropdownValue("");
    setDateValue("");
  }, [questionKey]);

  const options = dict?.options ?? [];
  const mode = dict?.mode;
  const type = dict?.type;

  const toggleMulti = (opt: string) =>
    setSelectedMulti((prev) =>
      prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt],
    );

  const submitFreeText = () => {
    const t = freeText.trim();
    if (!t || disabled) return;
    setFreeText("");
    void conv.send(t);
  };

  return (
    <div className={styles.container}>
      {/* Image / upload question */}
      {type === "image" && (
        <UploadPanel
          stagedFiles={conv.stagedFiles}
          onAddFiles={(f) => void conv.addFiles(f)}
          onRemoveFile={conv.removeFile}
          onSubmit={() => void conv.submitImageAnswer()}
          disabled={disabled}
        />
      )}

      {/* Date question */}
      {type === "date" && (
        <div className={styles.row}>
          <div className={styles.grow}>
            <Text size={200} className={styles.hint}>
              Choisissez une date
            </Text>
            <Input
              type="date"
              value={dateValue}
              onChange={(_, d) => setDateValue(d.value)}
              disabled={disabled}
            />
          </div>
          <Button
            appearance="primary"
            disabled={disabled || !dateValue}
            onClick={() => conv.confirmDate(dateValue)}
          >
            ✔ Confirmer
          </Button>
        </div>
      )}

      {/* Predefined answers — single select */}
      {options.length > 0 && mode === "one" && type !== "date" && type !== "image" && (
        <>
          <div className={styles.pills}>
            {options.map((opt) => (
              <ToggleButton
                key={opt}
                checked={selectedOne === opt}
                onClick={() => setSelectedOne(opt)}
                disabled={disabled}
              >
                {opt}
              </ToggleButton>
            ))}
          </div>
          {selectedOne && (
            <Button
              appearance="primary"
              disabled={disabled}
              onClick={() => conv.confirmChoice(selectedOne)}
            >
              Confirmer « {selectedOne} »
            </Button>
          )}
        </>
      )}

      {/* Predefined answers — multi select */}
      {options.length > 0 && mode === "multi" && type !== "date" && type !== "image" && (
        <>
          <div className={styles.pills}>
            {options.map((opt) => (
              <ToggleButton
                key={opt}
                checked={selectedMulti.includes(opt)}
                onClick={() => toggleMulti(opt)}
                disabled={disabled}
              >
                {opt}
              </ToggleButton>
            ))}
          </div>
          {selectedMulti.length > 0 && (
            <Button
              appearance="primary"
              disabled={disabled}
              onClick={() => conv.confirmMulti(selectedMulti)}
            >
              Confirmer ({selectedMulti.length})
            </Button>
          )}
        </>
      )}

      {/* Predefined answers — searchable dropdown */}
      {options.length > 0 && mode === "dropdown" && type !== "date" && type !== "image" && (
        <div className={styles.row}>
          <div className={styles.grow}>
            <Combobox
              freeform
              placeholder="Sélectionnez ou tapez votre choix..."
              value={dropdownValue}
              onOptionSelect={(_, d) => setDropdownValue(d.optionText ?? "")}
              onChange={(e) => setDropdownValue((e.target as HTMLInputElement).value)}
              disabled={disabled}
            >
              {options.map((opt) => (
                <Option key={opt}>{opt}</Option>
              ))}
            </Combobox>
          </div>
          <Button
            appearance="primary"
            disabled={disabled || !dropdownValue.trim()}
            onClick={() => conv.confirmChoice(dropdownValue.trim())}
          >
            Confirmer
          </Button>
        </div>
      )}

      {/* Free-text answer — always available (iso Streamlit chat_input) */}
      <form
        className={styles.freeText}
        onSubmit={(e) => {
          e.preventDefault();
          submitFreeText();
        }}
      >
        <div className={styles.grow}>
          <Input
            className={styles.grow}
            style={{ width: "100%" }}
            value={freeText}
            onChange={(_, d) => setFreeText(d.value)}
            placeholder="Votre réponse..."
            disabled={disabled}
          />
        </div>
        <Button appearance="secondary" type="submit" disabled={disabled || !freeText.trim()}>
          Envoyer
        </Button>
      </form>
    </div>
  );
}
